import { useEffect, useState, type KeyboardEvent, type RefObject } from 'react';

/**
 * The focusable status/readout boxes beside the arm controls (#1080: the
 * one-click status box and the fixed hotkey-target readout). When their text
 * overflows they take focus so the trader can scroll them. A key pressed in
 * one acts on the aimed card exactly as it does from the board, except the
 * box's own keys: its scroll keys, Space (scrolls the box; never pulls or
 * sends) and Escape (leaves the box; the aim stays). The boxes carry this
 * attribute, valued with which box it is (`Lt2ReadoutKind`), so anything the
 * listener says about a box names the right one.
 */
export const LT2_READOUT_ATTRIBUTE = 'data-lt2-readout';

export const LT2_READOUT_SELECTOR = `[${LT2_READOUT_ATTRIBUTE}]`;

export type Lt2ReadoutKind = 'status' | 'target';

const READOUT_NAME: Record<Lt2ReadoutKind, string> = {
  status: 'status box',
  target: 'target box',
};

/** The Tooltip quick reference both boxes show while they are focusable. */
export const LT2_READOUT_QUICK_REF = 'Scroll to read. Order keys still act on the aimed card.';

/**
 * The event the hotkey listener dispatches on a box to show a line in it
 * (`detail`: the text). The line is the box's own: it never touches the
 * strip's receipt.
 */
export const LT2_READOUT_LINE_EVENT = 'lt2-readout-line';

/**
 * The line a bound Space pressed in a box shows. Space scrolls the box there,
 * so the press the trader meant (a pull, or an order bound to Space) did not
 * happen; the line says so and names the way out. Colon form, recovery last,
 * and short enough that the recovery stays inside a two-line box at the
 * narrowest status box (no Attack control, ~213px).
 *
 * `aimed`: whether a game is aimed. With nothing aimed, Esc and Space would
 * only be refused for want of an aim, so the recovery starts with aiming.
 */
export function readoutSpaceLine(box: Element, action: 'pull' | 'send', aimed: boolean): string {
  const kind = box.getAttribute(LT2_READOUT_ATTRIBUTE);
  const name = kind === 'status' || kind === 'target' ? READOUT_NAME[kind] : 'box';
  return aimed
    ? `Space scrolls the ${name}: Esc, then Space to ${action}`
    : `Space scrolls the ${name}: aim a game, then Esc and Space to ${action}`;
}

/** Show `text` in the box (see {@link useReadoutBox}). */
export function showReadoutLine(box: Element, text: string): void {
  box.dispatchEvent(new CustomEvent<string>(LT2_READOUT_LINE_EVENT, { detail: text }));
}

/**
 * The line's look: the size notice's visible line, laid over the bottom of
 * the box. Out of flow, so nothing moves; at most two lines, so it stays
 * inside the box and never covers anything outside it.
 */
export const LT2_READOUT_LINE_CLASS =
  'bg-background text-negative pointer-events-none absolute inset-x-0 bottom-0 z-10 font-sans text-[11px] leading-tight';

/**
 * The keys a focused box keeps: scrolling. None of them can be bound as a
 * hotkey (the registry refuses arrows as reserved and the rest as system
 * keys), so keeping them costs the trader nothing.
 */
const READOUT_SCROLL_KEYS: ReadonlySet<string> = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]);

/**
 * One box's focus and line.
 *
 * `holdsFocus`: a box offers a focus stop while its text overflows OR while
 * it holds focus. Tying the stop to overflow alone lost the trader's place: a
 * short receipt replacing a long one under the caret removed the tabindex,
 * the browser dropped focus to the page, and the next Escape cleared the aim
 * instead of leaving the box.
 *
 * `line`: what the listener last asked the box to say. It belongs to the text
 * the box showed when it was said (`contentKey`), so a new receipt or target
 * is never hidden under it. Leaving the box, any key other than Space, or the
 * wheel puts it away: an order key fired from the box leaves the box's text as
 * it was, so the line must not stay over it.
 *
 * `onKeyDown`: only the scroll keys stop here. Escape blurs the box and still
 * bubbles; the listener leaves the aim alone for an Escape pressed in a box,
 * so a second Escape, from the board, is the one that clears it. Every other
 * key bubbles to the listener.
 */
export function useReadoutBox(
  ref: RefObject<HTMLElement | null>,
  contentKey: string,
): {
  holdsFocus: boolean;
  line: string | null;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onWheel: () => void;
} {
  const [holdsFocus, setHoldsFocus] = useState(false);
  const [line, setLine] = useState<{ text: string; contentKey: string } | null>(null);
  useEffect(() => {
    const box = ref.current;
    if (!box) return;
    const onLine = (event: Event) => {
      if (event instanceof CustomEvent && typeof event.detail === 'string') {
        setLine({ text: event.detail, contentKey });
      }
    };
    box.addEventListener(LT2_READOUT_LINE_EVENT, onLine);
    return () => box.removeEventListener(LT2_READOUT_LINE_EVENT, onLine);
  }, [ref, contentKey]);
  return {
    holdsFocus,
    line: line != null && line.contentKey === contentKey ? line.text : null,
    onFocus: () => setHoldsFocus(true),
    onBlur: () => {
      setHoldsFocus(false);
      setLine(null);
    },
    onKeyDown: (event) => {
      // Space is the one key that (re)shows the line; the listener hears it
      // after this handler, so clearing here never hides a fresh line.
      if (event.key !== ' ') setLine(null);
      if (READOUT_SCROLL_KEYS.has(event.key)) {
        event.stopPropagation();
        return;
      }
      if (event.key === 'Escape') event.currentTarget.blur();
    },
    onWheel: () => setLine(null),
  };
}
